// Admit-to-observation dialog — Phase 3 (2026-10-03).
//
// Opened from EncounterDetailScreen when the clinician decides a patient
// needs observation / short-stay. Fields are intentionally terse — this
// is a holding-area primitive, not a full admission note. Vitals cadence
// is a whitelist (ad-hoc / 15 / 30 / 60 / 120 min); the server rejects
// anything else. Location is a free-text label (bed 2, chair 5, corridor
// bench) because the clinic has no beds table yet.

import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { AdmitRequest } from '@pamosi/shared';
import { ApiError, admissionsApi } from '../lib/api';

interface Props {
    encounterId: string;
    onClose: () => void;
    onAdmitted: () => void;
}

const INTERVAL_OPTIONS: Array<{ value: number | null; label: string }> = [
    { value: null, label: 'Ad-hoc' },
    { value: 15, label: 'Every 15 min' },
    { value: 30, label: 'Every 30 min' },
    { value: 60, label: 'Every 1 hour' },
    { value: 120, label: 'Every 2 hours' },
];

export function AdmitDialog({ encounterId, onClose, onAdmitted }: Props) {
    const qc = useQueryClient();
    const [reason, setReason] = useState('');
    const [location, setLocation] = useState('');
    const [intervalMinutes, setIntervalMinutes] = useState<number | null>(60);
    const [expectedDuration, setExpectedDuration] = useState<string>('');
    const [error, setError] = useState<string | null>(null);

    const mut = useMutation({
        mutationFn: (body: AdmitRequest) => admissionsApi.admit(body),
        onSuccess: async () => {
            await Promise.all([
                qc.invalidateQueries({ queryKey: ['admissions'] }),
                qc.invalidateQueries({ queryKey: ['encounter', encounterId] }),
            ]);
            setError(null);
            onAdmitted();
        },
        onError: (err) => {
            setError(err instanceof ApiError ? err.message : 'Admit failed.');
        },
    });

    function submit() {
        setError(null);
        let duration: number | null | undefined = undefined;
        if (expectedDuration.trim() !== '') {
            const n = Number(expectedDuration.trim());
            if (!Number.isInteger(n) || n < 1 || n > 24 * 60) {
                setError('Expected duration must be between 1 and 1440 minutes.');
                return;
            }
            duration = n;
        }
        const body: AdmitRequest = {
            encounter_id: encounterId,
            source: 'post_consult',
            reason: reason.trim() === '' ? null : reason.trim(),
            location_label: location.trim() === '' ? null : location.trim(),
            vitals_interval_minutes: intervalMinutes,
            ...(duration !== undefined ? { expected_duration_minutes: duration } : {}),
        };
        mut.mutate(body);
    }

    return (
        <ModalShell title="Admit to observation" onClose={onClose}>
            <div className="space-y-3">
                <div>
                    <label className="pm-label">Reason (optional)</label>
                    <textarea
                        className="pm-input min-h-20"
                        value={reason}
                        onChange={(e) => setReason(e.target.value)}
                        placeholder="e.g. BP 170/110, keep for 2h recheck"
                        maxLength={2000}
                        disabled={mut.isPending}
                    />
                </div>
                <div>
                    <label className="pm-label">Location</label>
                    <input
                        className="pm-input"
                        type="text"
                        value={location}
                        onChange={(e) => setLocation(e.target.value)}
                        placeholder="e.g. Bed 2 / Chair 5 / Corridor bench"
                        maxLength={200}
                        disabled={mut.isPending}
                    />
                </div>
                <div className="grid grid-cols-2 gap-3">
                    <div>
                        <label className="pm-label">Vitals cadence</label>
                        <select
                            className="pm-input"
                            value={intervalMinutes == null ? '' : String(intervalMinutes)}
                            onChange={(e) => {
                                const v = e.target.value;
                                setIntervalMinutes(v === '' ? null : Number(v));
                            }}
                            disabled={mut.isPending}
                        >
                            {INTERVAL_OPTIONS.map((o) => (
                                <option key={String(o.value)} value={o.value == null ? '' : String(o.value)}>
                                    {o.label}
                                </option>
                            ))}
                        </select>
                    </div>
                    <div>
                        <label className="pm-label">Expected duration (min)</label>
                        <input
                            className="pm-input"
                            type="number"
                            min={1}
                            max={1440}
                            value={expectedDuration}
                            onChange={(e) => setExpectedDuration(e.target.value)}
                            placeholder="e.g. 120"
                            disabled={mut.isPending}
                        />
                    </div>
                </div>
                {error && <div className="pm-error">{error}</div>}
            </div>
            <div className="mt-4 flex justify-end gap-2">
                <button
                    type="button"
                    className="pm-btn-ghost"
                    onClick={onClose}
                    disabled={mut.isPending}
                >
                    Cancel
                </button>
                <button
                    type="button"
                    className="pm-btn-primary"
                    onClick={submit}
                    disabled={mut.isPending}
                >
                    {mut.isPending ? 'Admitting…' : 'Admit'}
                </button>
            </div>
        </ModalShell>
    );
}

// Minimal in-file modal shell. The clinic app has no shared Modal component
// yet; keep each dialog self-contained until a second use case is added
// that needs the same chrome.
export function ModalShell({
    title,
    onClose,
    children,
}: {
    title: string;
    onClose: () => void;
    children: React.ReactNode;
}) {
    return (
        <div
            className="fixed inset-0 z-50 flex items-start justify-center bg-slate-900/40 p-4 pt-20 overflow-y-auto"
            onClick={onClose}
            role="presentation"
        >
            <div
                className="pm-card w-full max-w-lg p-5 shadow-xl"
                onClick={(e) => e.stopPropagation()}
                role="dialog"
                aria-modal="true"
                aria-label={title}
            >
                <div className="flex items-center justify-between mb-3">
                    <div className="text-slate-900 text-sm font-semibold">{title}</div>
                    <button
                        type="button"
                        className="pm-btn-ghost pm-btn-sm text-slate-500"
                        onClick={onClose}
                        aria-label="Close"
                    >
                        ×
                    </button>
                </div>
                {children}
            </div>
        </div>
    );
}
